import { ChangeDetectionStrategy, Component, DestroyRef, HostListener, OnInit } from '@angular/core';
import { SelectProjectReportService } from '../service/select-project-report.service';
import { CsvMode, SelectProjectReportModel } from './select-project-report.model';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

enum PrimaryCsvMode {
  Grouped,
  ByPosition,
}

enum SecondaryCsvMode {
  ByPositionGrouped,
  ByPositionWithAccessories,
  ByPositionWithoutAccessories,
}

interface ViewOption<IdType> {
  value: IdType;
  id: string;
  name: string;
  label: string;
}

@Component({
  standalone: false,
  selector: 'select-csv-report',
  templateUrl: './select-csv-report.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectCsvReportComponent implements OnInit {

  readonly primaryCsvModeOptions: ViewOption<PrimaryCsvMode>[] = [
    { value: PrimaryCsvMode.Grouped, id: 'Primary0', name: 'Primary', label: 'selectCsvReportPopup.csvMode.grouped' },
    { value: PrimaryCsvMode.ByPosition, id: 'Primary1', name: 'Primary', label: 'selectCsvReportPopup.csvMode.byPosition' },
  ];

  readonly secondaryCsvModeOptions: ViewOption<SecondaryCsvMode>[] = [
    {
      value: SecondaryCsvMode.ByPositionGrouped,
      id: 'Secondary0',
      name: 'Secondary',
      label: 'selectCsvReportPopup.csvOption.byPosition.groupedByPosition',
    },
    {
      value: SecondaryCsvMode.ByPositionWithAccessories,
      id: 'Secondary1',
      name: 'Secondary',
      label: 'selectCsvReportPopup.csvOption.byPosition.withAccessories',
    },
    {
      value: SecondaryCsvMode.ByPositionWithoutAccessories,
      id: 'Secondary2',
      name: 'Secondary',
      label: 'selectCsvReportPopup.csvOption.byPosition.withoutAccessories',
    },
  ];

  primaryCsvMode: PrimaryCsvMode = PrimaryCsvMode.Grouped;
  secondaryCsvMode: SecondaryCsvMode = SecondaryCsvMode.ByPositionWithAccessories;
  isGrouped: boolean = true;
  isByPosition: boolean = false;

  constructor(
    public exportModel: SelectProjectReportModel,
    private bsModalRef: BsModalRef,
    private selectProjectReportService: SelectProjectReportService,
    private destroyRef: DestroyRef
  ) {}

  public ngOnInit(): void {
    this.loadCsvMode();

    // Make sure the initial report selection is right for the selected csvMode.
    this.updateReportSelection();

    this.exportModel.csvConfirm.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.close());
  }

  private updateFlags(): void {
    this.isGrouped = this.primaryCsvMode === PrimaryCsvMode.Grouped;
    this.isByPosition = this.primaryCsvMode === PrimaryCsvMode.ByPosition;
  }

  primaryChanged(): void {
    this.updateFlags();
    this.storeCsvMode();
  }

  secondaryChanged(): void {
    this.storeCsvMode();
  }

  private loadCsvMode(): void {
    switch (this.exportModel.selectedCsvMode) {
      case CsvMode.Grouped:
        this.primaryCsvMode = PrimaryCsvMode.Grouped;
        this.secondaryCsvMode = SecondaryCsvMode.ByPositionWithAccessories;
        break;

      case CsvMode.ByPositionGroupedWithoutAccessories:
        this.primaryCsvMode = PrimaryCsvMode.ByPosition;
        this.secondaryCsvMode = SecondaryCsvMode.ByPositionGrouped;
        break;

      case CsvMode.ByPositionWithAccessories:
        this.primaryCsvMode = PrimaryCsvMode.ByPosition;
        this.secondaryCsvMode = SecondaryCsvMode.ByPositionWithAccessories;
        break;

      case CsvMode.ByPositionWithoutAccessories:
        this.primaryCsvMode = PrimaryCsvMode.ByPosition;
        this.secondaryCsvMode = SecondaryCsvMode.ByPositionWithoutAccessories;
        break;

      default:
        // Unknown CsvMode, set to defaults.
        this.primaryCsvMode = PrimaryCsvMode.Grouped;
        this.secondaryCsvMode = SecondaryCsvMode.ByPositionWithAccessories;
        break;
    }
    this.updateFlags();
  }

  private updateReportSelection(): void {
    if (this.exportModel.reports.length === 2) {
      switch (this.exportModel.selectedCsvMode) {
        case CsvMode.Grouped:
          this.exportModel.reports[0].selected = true;
          this.exportModel.reports[1].selected = false;
          this.selectProjectReportService.onReportsChange();
          break;
        case CsvMode.ByPositionGroupedWithoutAccessories:
        case CsvMode.ByPositionWithAccessories:
        case CsvMode.ByPositionWithoutAccessories:
          this.exportModel.reports[0].selected = false;
          this.exportModel.reports[1].selected = true;
          this.selectProjectReportService.onReportsChange();
          break;
        default:
          break;
      }
    }
  }

  public storeCsvMode(): void {
    if (this.primaryCsvMode === PrimaryCsvMode.Grouped) {
      this.setCsvMode(CsvMode.Grouped);
      return;
    }

    // select the new report template
    if (this.exportModel.reports.length === 2) {
      this.exportModel.reports[0].selected = false;
      this.exportModel.reports[1].selected = true;
      this.selectProjectReportService.onReportsChange();
    }

    if (this.primaryCsvMode === PrimaryCsvMode.ByPosition) {
      if (this.secondaryCsvMode === SecondaryCsvMode.ByPositionGrouped) {
        this.setCsvMode(CsvMode.ByPositionGroupedWithoutAccessories);
        return;
      }

      if (this.secondaryCsvMode === SecondaryCsvMode.ByPositionWithAccessories) {
        this.setCsvMode(CsvMode.ByPositionWithAccessories);
        return;
      }

      if (this.secondaryCsvMode === SecondaryCsvMode.ByPositionWithoutAccessories) {
        this.setCsvMode(CsvMode.ByPositionWithoutAccessories);
        return;
      }
    }

    // Unknown primary- or secondaryCsvMode, don't store anything.
  }

  private setCsvMode(newMode: CsvMode): void {
    this.exportModel.selectedCsvMode = newMode;
    this.updateReportSelection();
    this.selectProjectReportService.onCsvModeChange();
  }

  public close(): void {
    this.bsModalRef.hide();
  }

  public next(): void {
    this.selectProjectReportService.next();
  }

  public onReportsChange(): void {
    this.selectProjectReportService.onReportsChange();
  }

  public dataStructureGroupChange(value: string): void {
    this.selectProjectReportService.dataStructureGroupChange(value);
  }

  @HostListener('click', ['$event'])
  protected onClick($event: any) {
    $event.stopPropagation();
  }
}
